import React from 'react';
import { compactJobDescriptionForParsing } from '../../../shared/utils';
import {
  jobApplicationService,
  type JobApplicationCreateRequest,
} from '../services/jobApplicationService';
import { secureOpenAIService } from '../services/secureOpenaiService';

export type SaveStatus =
  | 'idle'
  | 'checking'
  | 'saving'
  | 'saved'
  | 'exists'
  | 'error';

export interface ImportableSearchResult {
  title?: string;
  snippet?: string;
  url?: string;
  fullContent?: string;
}

const URL_EXISTENCE_CHECK_CONCURRENCY = 6;

const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

const getRecord = (value: unknown): Record<string, unknown> => {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
};

const buildCreateRequestFromParsed = (
  result: ImportableSearchResult,
  parsedData: Awaited<ReturnType<typeof secureOpenAIService.parseJobDescription>>,
): JobApplicationCreateRequest => {
  const description =
    result.fullContent && result.fullContent.trim()
      ? result.title
        ? `${result.title}\n\n${result.fullContent}`
        : result.fullContent
      : result.title && result.snippet
        ? `${result.title}\n\n${result.snippet}`
        : result.snippet || result.title || 'Imported from Intelligent Search';

  const parsedRecord = getRecord(parsedData);
  const rawParsedData = getRecord(parsedRecord.parsed_data);

  const getValue = (
    camelKey: string,
    snakeKey: string,
    fallback: unknown = null,
  ): unknown => {
    return (
      parsedRecord[camelKey] ??
      parsedRecord[snakeKey] ??
      rawParsedData[camelKey] ??
      rawParsedData[snakeKey] ??
      fallback
    );
  };

  const getArray = (camelKey: string, snakeKey: string): unknown[] => {
    const value = getValue(camelKey, snakeKey, []);
    return Array.isArray(value) && value.length > 0 ? value : [];
  };

  const looksLikeLocation = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }

    if (/\b\d{5}(?:-\d{4})?\b/.test(trimmed)) {
      return true;
    }

    if (/\b[A-Za-z].*,\s*[A-Za-z]{2}\b/.test(trimmed)) {
      return true;
    }

    return false;
  };

  const extractFromTitle = (title: string | undefined) => {
    if (!title) {
      return { company: null, position: null };
    }

    const atPattern = /^(.+?)\s+(?:at|@|-)\s+(.+)$/i;
    const dashPattern = /^(.+?)\s*-\s*(.+)$/;

    let position = title;
    let company = null;

    const atMatch = title.match(atPattern);
    if (atMatch) {
      position = atMatch[1].trim();
      const candidateCompany = atMatch[2].trim();
      company = looksLikeLocation(candidateCompany) ? null : candidateCompany;
    } else {
      const dashMatch = title.match(dashPattern);
      if (dashMatch) {
        position = dashMatch[1].trim();
        const candidateCompany = dashMatch[2].trim();
        company = looksLikeLocation(candidateCompany) ? null : candidateCompany;
      }
    }

    return { company, position };
  };

  const titleExtracted = extractFromTitle(result.title);
  const benefitsStructured = getRecord(
    parsedRecord.benefitsStructured ??
      parsedRecord.benefits_structured ??
      rawParsedData.benefitsStructured ??
      rawParsedData.benefits_structured ??
      {
        healthInsurance: [],
        retirement: [],
        timeOff: [],
        allowances: [],
        memberships: [],
        bonusesEquity: [],
        other: [],
      },
  );

  return {
    url: result.url ?? '',
    description,
    company:
      (getValue('company', 'company_name') as string | null) ||
      titleExtracted.company ||
      null,
    position:
      (getValue('position', 'job_title') as string | null) ||
      titleExtracted.position ||
      result.title ||
      null,
    positionCore: getValue('positionCore', 'position_core') as string | null,
    positionSpecialization: getArray(
      'positionSpecialization',
      'position_specialization',
    ) as string[],
    salary: getValue('salary', 'salary_range') as string | null,
    location: getValue('location', 'location') as string | null,
    workType:
      (getValue('workType', 'work_type') as JobApplicationCreateRequest['workType']) ||
      (getValue(
        'employmentType',
        'employment_type',
      ) as JobApplicationCreateRequest['workType']),
    employmentType: getValue(
      'employmentType',
      'employment_type',
    ) as JobApplicationCreateRequest['employmentType'],
    contactInfo: getValue('contactInfo', 'contact_info') as string | null,
    recruiter: getValue('recruiter', 'recruiter') as string | null,
    jobLevel:
      (getValue('jobLevel', 'job_level') as string | null) ||
      (getValue('experienceLevel', 'experience_level') as string | null),
    jobLevelNormalized: getValue(
      'jobLevelNormalized',
      'job_level_normalized',
    ) as string | null,
    jobLevelConfidence: getValue(
      'jobLevelConfidence',
      'job_level_confidence',
    ) as number | null,
    jobLevelSource: getValue('jobLevelSource', 'job_level_source') as string | null,
    jobSummary:
      (getValue('jobSummary', 'job_description_summary') as string | null) ||
      (getValue('jobSummary', 'job_summary') as string | null),
    responsibilities: getArray('responsibilities', 'responsibilities') as string[],
    requiredQualifications: getArray(
      'requiredQualifications',
      'required_qualifications',
    ) as string[],
    optionalQualifications: getArray(
      'optionalQualifications',
      'optional_qualifications',
    ) as string[],
    additionalQualifications: getArray(
      'additionalQualifications',
      'additional_qualifications',
    ) as string[],
    education: getArray('education', 'education') as JobApplicationCreateRequest['education'],
    technologies: getArray('technologies', 'skills_required') as string[],
    benefits: getArray('benefits', 'benefits') as string[],
    benefitsStructured: {
      healthInsurance: Array.isArray(benefitsStructured.healthInsurance)
        ? (benefitsStructured.healthInsurance as string[])
        : [],
      retirement: Array.isArray(benefitsStructured.retirement)
        ? (benefitsStructured.retirement as string[])
        : [],
      timeOff: Array.isArray(benefitsStructured.timeOff)
        ? (benefitsStructured.timeOff as string[])
        : [],
      allowances: Array.isArray(benefitsStructured.allowances)
        ? (benefitsStructured.allowances as string[])
        : [],
      memberships: Array.isArray(benefitsStructured.memberships)
        ? (benefitsStructured.memberships as string[])
        : [],
      bonusesEquity: Array.isArray(benefitsStructured.bonusesEquity)
        ? (benefitsStructured.bonusesEquity as string[])
        : [],
      other: Array.isArray(benefitsStructured.other)
        ? (benefitsStructured.other as string[])
        : [],
    },
    postingDate: getValue('postingDate', 'posting_date') as string | null,
    postingAge: getValue('postingAge', 'posting_age') as string | null,
    applicantsCount: getValue('applicantsCount', 'applicants_count') as number | null,
    applicantsText: getValue('applicantsText', 'applicants_text') as string | null,
    appliedDate: getTodayDate(),
    status: 'Pending',
  };
};

export const getJobImportActionLabel = (status: SaveStatus): string => {
  switch (status) {
    case 'checking':
      return 'Checking...';
    case 'saving':
      return 'Adding...';
    case 'saved':
      return 'Added';
    case 'exists':
      return 'Already Added';
    case 'error':
      return 'Try Again';
    default:
      return 'Add Job Application';
  }
};

const isRetryableGatewayError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('504') ||
    message.toLowerCase().includes('timeout') ||
    message.toLowerCase().includes('gateway')
  );
};

export const useSearchResultApplicationImport = ({
  isEnabled,
  results,
}: {
  isEnabled: boolean;
  results: ImportableSearchResult[];
}) => {
  const [saveStatusByUrl, setSaveStatusByUrl] = React.useState<Record<string, SaveStatus>>(
    {},
  );
  const [saveErrorByUrl, setSaveErrorByUrl] = React.useState<Record<string, string>>(
    {},
  );

  React.useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const urls = results
      .map((result) => result.url)
      .filter((url): url is string => Boolean(url));

    if (urls.length === 0) {
      setSaveStatusByUrl({});
      setSaveErrorByUrl({});
      return;
    }

    const uniqueUrls = Array.from(new Set(urls));

    setSaveStatusByUrl((prev) => {
      const next: Record<string, SaveStatus> = {};
      uniqueUrls.forEach((url) => {
        next[url] = prev[url] === 'saved' ? 'saved' : 'checking';
      });
      return next;
    });
    setSaveErrorByUrl({});

    let isCancelled = false;

    const checkExistingApplications = async () => {
      const entries: Array<{ url: string; exists: boolean }> = [];

      for (let index = 0; index < uniqueUrls.length; index += URL_EXISTENCE_CHECK_CONCURRENCY) {
        if (isCancelled) {
          return;
        }

        const batch = uniqueUrls.slice(index, index + URL_EXISTENCE_CHECK_CONCURRENCY);
        const batchEntries = await Promise.all(
          batch.map(async (url) => {
            const existing = await jobApplicationService.findApplicationByUrl(url);
            return { url, exists: Boolean(existing) };
          }),
        );
        entries.push(...batchEntries);
      }

      if (isCancelled) {
        return;
      }

      setSaveStatusByUrl((prev) => {
        const next = { ...prev };
        entries.forEach(({ url, exists }) => {
          if (next[url] === 'saved') {
            return;
          }
          next[url] = exists ? 'exists' : 'idle';
        });
        return next;
      });
    };

    void checkExistingApplications();

    return () => {
      isCancelled = true;
    };
  }, [isEnabled, results]);

  const setSaveStatus = React.useCallback((url: string, status: SaveStatus) => {
    setSaveStatusByUrl((prev) => ({ ...prev, [url]: status }));
  }, []);

  const setSaveError = React.useCallback((url: string, message: string | null) => {
    setSaveErrorByUrl((prev) => {
      if (!message) {
        const { [url]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [url]: message };
    });
  }, []);

  const handleAddToApplications = React.useCallback(
    async (result: ImportableSearchResult) => {
      if (!result.url) {
        return;
      }

      const currentStatus = saveStatusByUrl[result.url];
      if (
        currentStatus === 'checking' ||
        currentStatus === 'saving' ||
        currentStatus === 'saved' ||
        currentStatus === 'exists'
      ) {
        return;
      }

      setSaveStatus(result.url, 'saving');
      setSaveError(result.url, null);

      const maxAttempts = 2;
      const retryDelayMs = 1500;
      let cachedParsedData: Awaited<
        ReturnType<typeof secureOpenAIService.parseJobDescription>
      > | null = null;
      let cachedRequest: JobApplicationCreateRequest | null = null;
      let createWasCalled = false;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const existing = await jobApplicationService.findApplicationByUrl(result.url);
          if (existing) {
            setSaveStatus(result.url, 'exists');
            return;
          }

          if (!cachedParsedData) {
            let jobDescription = '';
            if (result.fullContent && result.fullContent.trim()) {
              jobDescription = result.title
                ? `${result.title}\n\n${result.fullContent}`
                : result.fullContent;
            } else {
              jobDescription =
                result.title && result.snippet
                  ? `${result.title}\n\n${result.snippet}`
                  : result.snippet || result.title || '';
            }

            if (!jobDescription.trim()) {
              throw new Error('No job description available to parse');
            }

            const toParse = compactJobDescriptionForParsing(jobDescription);
            cachedParsedData = await secureOpenAIService.parseJobDescription(toParse);
            cachedRequest = buildCreateRequestFromParsed(result, cachedParsedData);
          }

          createWasCalled = true;
          await jobApplicationService.createApplication(cachedRequest!);
          setSaveStatus(result.url, 'saved');
          return;
        } catch (requestError) {
          const message =
            requestError instanceof Error
              ? requestError.message
              : 'Failed to add job application.';

          if (createWasCalled && isRetryableGatewayError(requestError)) {
            await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            const found = await jobApplicationService.findApplicationByUrl(result.url);
            if (found) {
              setSaveStatus(result.url, 'exists');
            } else {
              setSaveStatus(result.url, 'error');
              setSaveError(
                result.url,
                'Request may have succeeded. Check your job applications list.',
              );
            }
            return;
          }

          const retryable = attempt < maxAttempts && isRetryableGatewayError(requestError);
          if (retryable) {
            await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            continue;
          }

          setSaveStatus(result.url, 'error');
          setSaveError(result.url, message);
          return;
        }
      }
    },
    [saveErrorByUrl, saveStatusByUrl, setSaveError, setSaveStatus],
  );

  return {
    saveStatusByUrl,
    saveErrorByUrl,
    handleAddToApplications,
  };
};
